import { Card } from 'components/common';
import { Col, Grid } from 'components/common/Grid';
import { CardLoader } from 'components/common/Loading';
import { PageHelmet } from 'components/common/PageHelmet';
import { TimePlanningDayPicker } from 'components/views-components/staff/settings/timeplanning/TimePlanningDayPicker';
import { useMemo } from 'react';

import { TimePlanner } from '../../../../components/TimePlanner/TimePlanner';
import { timePlannerHasStaffOnlyItems } from '../../../../components/TimePlanner/timePlannerUtils';
import { TimePlanningHeader } from '../../../../components/views-components/staff/settings/timeplanning/TimePlanningHeader';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { hideIf } from '../../../../styles/displayUtils';
import { formatDateOnlyISO, parseIsoDate } from '../../../../utils/dateUtil';
import { StaffLayout } from '../../StaffLayout';
import { useStaffTimeDayPickerData } from './StaffTimeDayPickerHooks';
import { TemplateApptCountsCard } from './timePlanning/TemplateApptCountsCard';

export const StaffTimeDayPicker = () => {
  const { t } = useCommonTranslation();

  const {
    selectedDate,
    updateDates,
    defaultTimePlanning: {
      showOnline,
      defaultTemplate,
      setShowOnline,
      saveTimePlanning,
    },
    isPlannerLoading,
    isDayPickerLoading,
    plannerApptTemplatesData,
    dayPickerApptTemplatesData,
    plannerFilterDate,
    dayPickerFilterDate,
    cubicleCount,
  } = useStaffTimeDayPickerData();

  const dayPickerDatesWithDefaultsChanged = useMemo(
    () =>
      dayPickerApptTemplatesData?.data?.map((template) =>
        parseIsoDate(template.template_date),
      ) || [],
    [dayPickerApptTemplatesData],
  );

  const plannerApptTemplate = useMemo(
    () =>
      plannerApptTemplatesData?.data?.find(
        (template) =>
          template.template_date === formatDateOnlyISO(selectedDate),
      ),
    [selectedDate, plannerApptTemplatesData],
  );

  const showOnlineMustBeShown = timePlannerHasStaffOnlyItems(
    defaultTemplate || plannerApptTemplate,
  );

  const minDate = new Date();

  return (
    <StaffLayout>
      <PageHelmet title={t('time-planning')} />
      <Grid>
        <Col
          md={5}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--s5)',
          }}
        >
          <Card>
            {isDayPickerLoading && <CardLoader />}
            <div style={hideIf(isDayPickerLoading)}>
              <TimePlanningDayPicker
                selectedDate={selectedDate}
                filterDate={dayPickerFilterDate}
                updateDates={updateDates}
                datesWithChangesFromDefault={dayPickerDatesWithDefaultsChanged}
                minDate={minDate}
              />
            </div>
          </Card>
          <TemplateApptCountsCard
            showOnlineMustBeShown={showOnlineMustBeShown}
            selectedDate={selectedDate}
            isLoading={isPlannerLoading}
          />
        </Col>
        <Col md={7}>
          <Card>
            {isPlannerLoading ? (
              <CardLoader />
            ) : (
              <>
                <TimePlanningHeader
                  setShowOnline={setShowOnline}
                  showOnline={showOnline}
                  showOnlineMustBeShown={showOnlineMustBeShown}
                />
                <TimePlanner
                  cubicleCount={cubicleCount || 0}
                  dateFormat="fullMonthWithYear"
                  filterDate={plannerFilterDate}
                  minDate={minDate}
                  showOnline={showOnline}
                  setShowOnline={setShowOnline}
                  selectedDate={selectedDate}
                  setSelectedDate={(date) =>
                    updateDates({ selectedDate: date })
                  }
                  submitTimePlanner={saveTimePlanning}
                  defaultTemplate={defaultTemplate}
                  apptTemplate={plannerApptTemplate}
                />
              </>
            )}
          </Card>
        </Col>
      </Grid>
    </StaffLayout>
  );
};
